import type React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Separator,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import {
    StepBackButton,
    WorkConfirmDialog,
} from '@/pages/catalogImport/components';
import { countParam } from '@/pages/catalogImport/notices';
import type { CatalogImportMode } from '@/pages/catalogImport/store';
import { TEMPLATE_BODY_ROWS } from '@/pages/catalogImport/template';
import {
    hasWork,
    WORK_AND_KEY,
    workCountsLine,
    workPartsOf,
    workSentenceOf,
    type ImportWork,
    type WorkWording,
} from '@/pages/catalogImport/work';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ChosenFileCard,
    UploadBlockAlert,
    UploadDropZone,
    UploadGuidance,
    UploadRefusalAlert,
    UploadWarningAlerts,
    type ChosenFileProgress,
} from './components';
import { isUploadBusy, type UploadIntakeState } from './uploadState';
import type { UseUploadStepResult } from './useUploadStep';

type UploadStepViewProps = Omit<UseUploadStepResult, 'hasBranch'> & {
    /**
     * Back opens Branch in the wizard ("← Branch"); on the in-shell page it
     * is the exit Back to the branch list ("To Branches" and its hint).
     */
    mode: CatalogImportMode;
};

type FileIntake = Exclude<UploadIntakeState, { status: 'choosing' }>;

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/** The work's counts in the form's words (`workPartsOf`), zero counts dropped. */
const workWordsOf = (
    t: TFunction,
    work: ImportWork,
    wording: WorkWording,
): string[] =>
    workPartsOf(work, wording).map(({ key, params }) => t(key, params));

/**
 * The line under the file's name. A checked file the owner worked on counts
 * the work beside its rows, zero counts dropped ("40 rows · 3 edited · 1
 * excluded · 2 name choices").
 */
const fileStatusOf = (
    t: TFunction,
    intake: FileIntake,
    work: ImportWork,
): string | null => {
    switch (intake.status) {
        case 'reading':
            return intake.progress
                ? t('catalogImport.upload.reading.progress', {
                      parsed: countParam(intake.progress.parsedRows),
                      total: countParam(intake.progress.totalRows),
                  })
                : t('catalogImport.upload.reading.status');
        case 'checking':
            return t('catalogImport.upload.checking.status', {
                rows: countParam(intake.rowCount),
            });
        case 'checked':
            return hasWork(work)
                ? t('catalogImport.upload.file.rowsWithWork', {
                      rows: countParam(intake.rowCount),
                      work: workCountsLine(workWordsOf(t, work, 'counts')),
                  })
                : t('catalogImport.upload.file.rowsRead', {
                      rows: countParam(intake.rowCount),
                  });
        case 'blocked':
            return intake.rowCount === null
                ? null
                : t('catalogImport.upload.file.rowCount', {
                      rows: countParam(intake.rowCount),
                  });
    }
};

/** Rows against the total once the worker reports them; a moving bar before, and while checking. */
const fileProgressOf = (
    t: TFunction,
    intake: FileIntake,
): ChosenFileProgress | null => {
    if (intake.status === 'reading') {
        const label = t('catalogImport.upload.reading.progressLabel', {
            file: intake.fileName,
        });
        return intake.progress && intake.progress.totalRows > 0
            ? {
                  kind: 'rows',
                  label,
                  value: intake.progress.parsedRows,
                  max: intake.progress.totalRows,
              }
            : { kind: 'indeterminate', label };
    }
    if (intake.status === 'checking') {
        return {
            kind: 'indeterminate',
            label: t('catalogImport.upload.checking.progressLabel', {
                file: intake.fileName,
            }),
        };
    }
    return null;
};

/**
 * Upload: the title and the limit, then the three-step guidance beside the
 * drop zone (stacked below `lg`). Once a file is accepted, its card takes the
 * drop zone's place: reading it (rows so far, Cancel), checking it, and then
 * either blocked (the reason) or checked (any warnings), with Other file.
 *
 * Under both, a footer behind a separator, in every state: Back leftmost,
 * then the line saying Continue comes once a file is read (drop zone) or
 * Continue rightmost (disabled until the file is checked). While the file is
 * read or checked, Back and Continue are disabled: Cancel is the only
 * action. On a phone the footer is a column, Continue first and Back last;
 * on the drop zone, Back and then the line. On the in-shell page (`mode`)
 * Back is the exit Back to the branch list, where there is no Branch step.
 *
 * A checked file's card counts the owner's work beside its rows, and once
 * the file is read (blocked or checked) a new file can be dropped over the
 * card. Replacing a file with work asks first ("Replace the file?", the
 * work it loses, a danger Replace file) from the hook's `replaceConfirm`.
 */
export const UploadStepView: React.FC<UploadStepViewProps> = ({
    mode,
    isPreparingTemplate,
    templateFailed,
    onDownloadTemplate,
    intake,
    work,
    onFilesPicked,
    onDrop,
    onDiscardFile,
    replaceConfirm,
    onKeepFile,
    onConfirmReplace,
    onRetry,
    onBack,
    onContinue,
}) => {
    const { t } = useTranslation('pages');
    // The same grouping as the import's sentences (`2,000`), whatever the language.
    const limit = countParam(TEMPLATE_BODY_ROWS);

    const isBusy = isUploadBusy(intake);
    const isChoosing = intake.status === 'choosing';

    const renderFile = (file: FileIntake) => (
        <ChosenFileCard
            action={
                isBusy
                    ? {
                          label: t('catalogImport.upload.reading.cancel'),
                          testId: 'catalog-import-chosen-file-cancel',
                          onPress: onDiscardFile,
                      }
                    : {
                          label: t('catalogImport.upload.file.other'),
                          testId: 'catalog-import-chosen-file-other',
                          onPress: onDiscardFile,
                      }
            }
            fileName={file.fileName}
            onDrop={isBusy ? undefined : onDrop}
            progress={fileProgressOf(t, file)}
            status={fileStatusOf(t, file, work)}
        >
            {(file.status === 'blocked' || file.status === 'checked') &&
            file.refusal ? (
                <UploadRefusalAlert refusal={file.refusal} />
            ) : null}
            {file.status === 'blocked' ? (
                <UploadBlockAlert block={file.block} onRetry={onRetry} />
            ) : null}
            {file.status === 'blocked' || file.status === 'checked' ? (
                <UploadWarningAlerts warnings={file.warnings} />
            ) : null}
        </ChosenFileCard>
    );

    // On a phone the drop zone's Back comes first, its line under it; with
    // a file, Continue comes first and Back last.
    const backClassName = isChoosing
        ? 'w-full sm:w-auto'
        : 'order-2 w-full sm:order-1 sm:w-auto';
    const back =
        mode === 'shell' ? (
            <StepBackButton
                appearance="exit"
                className={backClassName}
                isDisabled={isBusy}
                onPress={onBack}
            />
        ) : (
            <StepBackButton
                className={backClassName}
                isDisabled={isBusy}
                onPress={onBack}
                target="branch"
            />
        );

    return (
        <section
            className="flex flex-col gap-8 py-4 sm:py-8"
            {...getNodeTestIdProp('catalog-import-upload')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('catalog-import-upload-title')}
                >
                    {t('catalogImport.upload.title')}
                </Typography>
                <Typography.Paragraph color="muted">
                    {t('catalogImport.upload.intro', { limit })}
                </Typography.Paragraph>
            </div>
            <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-10">
                <UploadGuidance
                    isPreparingTemplate={isPreparingTemplate}
                    limit={limit}
                    onDownloadTemplate={onDownloadTemplate}
                    templateFailed={templateFailed}
                />
                {intake.status === 'choosing' ? (
                    <UploadDropZone
                        limit={limit}
                        onDrop={onDrop}
                        onFilesPicked={onFilesPicked}
                        refusal={intake.refusal}
                    />
                ) : (
                    renderFile(intake)
                )}
            </div>
            <div className="flex flex-col gap-4">
                <Separator />
                <div
                    className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                    {...getNodeTestIdProp('catalog-import-upload-footer')}
                >
                    {back}
                    {isChoosing ? (
                        <Typography
                            color="muted"
                            type="body-xs"
                            {...getNodeTestIdProp(
                                'catalog-import-upload-continue-hint',
                            )}
                        >
                            {t('catalogImport.upload.continueHint')}
                        </Typography>
                    ) : (
                        <Button
                            className="order-1 w-full sm:order-2 sm:w-auto"
                            isDisabled={intake.status !== 'checked'}
                            onPress={onContinue}
                            variant="primary"
                            {...getNodeTestIdProp(
                                'catalog-import-upload-continue',
                            )}
                        >
                            {t('catalogImport.upload.file.continue')}
                        </Button>
                    )}
                </div>
            </div>
            <WorkConfirmDialog
                actionLabel={t('catalogImport.upload.replace.confirm')}
                body={t('catalogImport.upload.replace.body', {
                    work: replaceConfirm
                        ? workSentenceOf(
                              workWordsOf(t, replaceConfirm.work, 'lose'),
                              t(WORK_AND_KEY),
                          )
                        : '',
                })}
                isOpen={replaceConfirm !== null}
                keepLabel={t('catalogImport.upload.replace.keep')}
                onConfirm={onConfirmReplace}
                onKeep={onKeepFile}
                title={t('catalogImport.upload.replace.title')}
                tone="danger"
            />
        </section>
    );
};
